import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    Output,
    SimpleChange,
    SimpleChanges,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ImageProfile } from '@cds/betting-offer';
import { AutomationModule } from '@frontend/sports/automation/core-feature';
import { CalendarConfig, PrettyUrlsConfig, PriceBoostConfig, TimeFilterConfig } from '@frontend/sports/common/client-config-data-access';
import { CountItem } from '@frontend/sports/common/core/data-access/sport-model';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { ListItemComponent } from '@frontend/sports/common/ui/sports-list-items';
import { CompetitionLogoComponent, CompetitionLogosService } from '@frontend/sports/competition-logos/feature';
import { ItemTreeComponent, TreeItem } from '@frontend/sports/item/feature';
import { CompetitionRoute } from '@frontend/sports/navigation/core/feature/models';
import { UrlParam } from '@frontend/sports/navigation/core/feature/url-builder';
import { PriceboostCountSignpostComponent } from '@frontend/sports/price-boost/feature';
import { TooltipModule } from '@frontend/sports/tooltip-feature';
import { TrackReferringACtionDirective, TrackingSource } from '@frontend/sports/tracking/feature';
import { Tournament } from '@frontend/sports/types/components/tournaments';
import { DsDivider } from '@frontend/ui/divider';
import { RxIf } from '@rx-angular/template/if';
import { isEqual } from 'lodash-es';

import { CalendarItemListComponent } from '../calendar/calendar-item-list.component';
import { CalendarPage, CalendarService } from '../calendar/calendar.service';
import { IntervalLink } from '../calendar/count-interval.service';
import { TimeFrameLocation } from '../calendar/time-filters-tracking.service';
import { BadgeComponent } from '../crm-promotion/badge.component';
import { CrmPromotionProviderService, PromotionMap } from '../crm-promotion/crm-promotion-provider.service';
import { FavouriteLeagueToggleComponent } from '../favourites/toggle/favourite-league-toggle.component';
import { LeagueTreeItem, SportTreeState, VirtualGroupTreeItem } from './competition-list.models';
import { CompetitionStoreService, TreeSource } from './competition-store.service';
import { CompetitionTreeService } from './competition-tree.service';

@Component({
    selector: 'ms-competition-tree',
    templateUrl: 'competition-tree.component.html',
    styleUrl: './competition-tree.component.scss',
    imports: [
        FavouriteLeagueToggleComponent,
        TooltipModule,
        PriceboostCountSignpostComponent,
        BadgeComponent,
        CompetitionLogoComponent,
        NgTemplateOutlet,
        ItemTreeComponent,
        RxIf,
        AutomationModule,
        CalendarItemListComponent,
        ListItemComponent,
        DsDivider,
        TrackReferringACtionDirective,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompetitionTreeComponent implements OnChanges {
    @HostBinding('class') className = 'competition-tree';
    @Input() sport: CountItem;
    @Input() context?: CompetitionRoute;
    @Input() tournaments?: Tournament[];
    @Input() stateless = false;
    @Input() topCompetition = false;
    @Input() showFavourite = false;
    @Input() treeItemNavigation = false;
    @Input() eSportsLobby = false;
    @Output() treeItemClicked = new EventEmitter<TreeItem>();
    readonly timeFrameLocation = TimeFrameLocation.CompetitionsPage;
    treeSource = TreeSource;
    favouriteTracking: TrackingSource = { source: 'Top Competition' };
    trackingValue = signal<string>('M2_ListItemlinks');
    cleanedAfterTrack = signal(false);

    tree?: TreeItem;
    top?: TreeItem;
    all?: TreeItem;
    promoCompetitions: StringDictionary<number> = {};
    calendarIntervals: IntervalLink[];

    constructor(
        private mapper: CompetitionTreeService,
        private store: CompetitionStoreService,
        private competitionLogoService: CompetitionLogosService,
        private crmPromotionProvider: CrmPromotionProviderService,
        private changeDetectorRef: ChangeDetectorRef,
        private calendar: CalendarService,
        private timeFilterConfig: TimeFilterConfig,
        private urlConfig: PrettyUrlsConfig,
        private calendarConfig: CalendarConfig,
        public priceBoostConfig: PriceBoostConfig,
    ) {
        this.crmPromotionProvider.promotionsMap$.pipe(takeUntilDestroyed()).subscribe((ids) => this.promotionMapChanged(ids));
    }

    ngOnChanges(changes: SimpleChanges): void {
        const state = this.getState(false, this.eSportsLobby);
        this.calendarIntervals = [];
        if (this.isChanged(changes.sport, this.isSportEqual)) {
            this.tree = this.mapper.get(this.sport, state.context, this.eSportsLobby);
            this.top = !this.topCompetition ? this.mapper.getTopCompetitions(this.sport, this.tournaments || [], state.context) : undefined;
        } else if (this.isChanged(changes.tournaments)) {
            this.top = !this.topCompetition ? this.mapper.getTopCompetitions(this.sport, this.tournaments || [], state.context) : undefined;
        }

        if (this.isChanged(changes.context) || this.isChanged(changes.stateless) || this.isChanged(changes.eSportsLobby)) {
            this.tree = this.mapper.setState(this.tree, state);
            this.top = !this.topCompetition ? this.mapper.setState(this.top, this.getState(true)) : undefined;

            this.all = this.mapper.getAll(this.sport, state);
        }

        if (this.sport) {
            this.store.init(this.sport.id);
        }
        if (this.showCalendarTimeFilters(state)) {
            const sport = this.context ? { id: this.context.sport, name: this.context.sportName } : undefined;
            this.calendarIntervals = this.calendar.getCalendarLinks(CalendarPage.Competitions, sport as UrlParam);
        }
    }

    isLeagueOrVirtualGroup(item: TreeItem): boolean {
        return item instanceof LeagueTreeItem || item instanceof VirtualGroupTreeItem;
    }

    isPriceBoost(isCountPresent: boolean): boolean {
        return this.priceBoostConfig.isEnabled && isCountPresent;
    }

    getItemFavouriteId(item: TreeItem): string | number {
        if (item instanceof VirtualGroupTreeItem) {
            return item.favouriteId;
        } else if (item instanceof LeagueTreeItem) {
            return item.realCompetitionId || item.id;
        }

        return item.id;
    }

    toggled(source: TreeSource, tree: TreeItem, item: TreeItem): void {
        if (tree === this.tree) {
            this.tree = this.mapper.toggleExpanded(this.tree, item);
        }
        if (tree === this.top) {
            this.top = this.mapper.toggleExpanded(this.top, item);
        }

        this.changeDetectorRef.markForCheck();

        if (this.stateless) {
            return;
        }

        this.store.toggle(source, item);
    }

    toggleExpandedAll(item: TreeItem): void {
        this.all = this.mapper.toggleExpanded(this.all!, item);
    }

    hasCompetitionLogo(sportId: number, leagueId: number, imageProfile: ImageProfile): boolean {
        return !!imageProfile || this.competitionLogoService.hasLogo(sportId, leagueId);
    }

    isPromoted(item: TreeItem): boolean {
        return item instanceof LeagueTreeItem && item.isVirtual
            ? item.siblings!.some((id) => !!this.promoCompetitions[id])
            : !!this.promoCompetitions[item.id];
    }

    getSubTitle(showSubTitle: boolean, item: LeagueTreeItem): string | undefined {
        return showSubTitle && this.isLeagueOrVirtualGroup(item) ? item.region : undefined;
    }

    getCount(isTopCompetition: boolean, itemCount: number): number | undefined {
        return isTopCompetition ? undefined : itemCount;
    }

    private promotionMapChanged(map: PromotionMap): void {
        this.promoCompetitions = map.competitions;
        this.changeDetectorRef.markForCheck();
    }

    private isChanged = (change: SimpleChange, equality: (first: any, second: any) => boolean = isEqual) => {
        return change && !equality(change.currentValue, change.previousValue);
    };

    private isSportEqual = (first: CountItem, second: CountItem) => {
        if (first === undefined && second === undefined) {
            return true;
        }

        if (!first || !second || first.id !== second.id || first.children.length !== second.children.length) {
            return false;
        }

        if (!isEqual(first.counts, second.counts)) {
            return false;
        }

        for (let i = 0; i < first.children.length; i++) {
            if (!this.isSportEqual(first.children[i], second.children[i])) {
                return false;
            }
        }

        return true;
    };

    private getState(top: boolean = false, esportsLobby: boolean = false): SportTreeState {
        if (!this.sport) {
            return {};
        }

        const state = this.store.get(this.sport.id);
        const context = this.context || {};

        if (this.stateless) {
            state.top = false;
            state.all = this.topCompetition;
            state.children = [];
            context.sport = this.sport.id;
        }

        const treeState = {
            sport: context.sport,
            sportExpanded: top ? state.top : state.all,
            region: context.region,
            regionExpanded: top ? undefined : state.children,
            league: context.league,
            tournament: context.tournament,
            isVirtual: context.isVirtual,
            virtualCompetitionGroup: context.virtualCompetitionGroup,
            context: context.context,
        };

        //we need to adapt the treeState when on esportsLobby, as context is null and the functionality for "1st expanded region" does not work-
        //context is CompetitionRoute, and we cannot build if from EsportsLobby as we have multiple sports and competitions
        if (esportsLobby) {
            treeState.sport = this.sport.id;
            treeState.sportExpanded = false;
            treeState.regionExpanded = this.sport.children.length > 0 ? [this.sport.children[0].id] : [];
        }

        return treeState;
    }

    onTreeItemClick(item: TreeItem): void {
        this.cleanedAfterTrack.update(() => item.href?.indexOf('http') === 0 || false);
        this.treeItemClicked.emit(item);
    }

    private showCalendarTimeFilters(state: SportTreeState): boolean {
        return (
            state.context === this.urlConfig.translations.betting && this.timeFilterConfig.isTimeFilterEnabled && this.calendarConfig.showCalendarTab
        );
    }
}
